// The authored half of a map -> the whole map, by the 180° point symmetry (x, z) -> (-x, -z).
import type { Den, MapData, MapObjects, Pt } from './schema.js';

/* ---- the mirror, per object type ---- */
// Ellipses keep their rot (an ellipse turned by π is itself); rectangles and decor turn with the map so a
// building's door or a grave's face points the mirrored way too.
export const mirrorPt = ([x, z]: Pt): Pt => [-x, -z];
const mirrorLine = <T extends { points: Pt[] }>(o: T): T => ({ ...o, points: o.points.map(mirrorPt) });

export function mirrorObjects(o: MapObjects): MapObjects {
  return {
    camps: o.camps.map(mirrorPt),
    bridges: o.bridges.map(mirrorPt),
    trees: o.trees.map(mirrorPt),
    rocks: o.rocks.map(mirrorPt),
    pillars: o.pillars.map(mirrorPt),
    walls: o.walls.map(mirrorLine),
    buildings: o.buildings.map(b => ({ ...b, x: -b.x, z: -b.z, rot: b.rot + Math.PI })),
    streams: o.streams.map(mirrorLine),
    ponds: o.ponds.map(poly => poly.map(mirrorPt)),
    cliffs: o.cliffs.map(mirrorLine),
    brush: o.brush.map(b => ({ ...b, x: -b.x, z: -b.z })),
    roads: o.roads.map(mirrorLine),
    decor: o.decor.map(d => ({ ...d, x: -d.x, z: -d.z, rot: d.rot + Math.PI })),
    props: o.props.map(p => ({ ...p, x: -p.x, z: -p.z, rot: p.rot + Math.PI })),
    fields: o.fields.map(f => ({ ...f, x: -f.x, z: -f.z, rot: f.rot + Math.PI })),
    fords: o.fords.map(f => ({ ...f, x: -f.x, z: -f.z, rot: f.rot + Math.PI })),
  };
}

/** True when p is on the author's side of the base-to-base diagonal, or within `tolerance` metres past it. */
export function inAuthoredHalf(map: Pick<MapData, 'base'>, [x, z]: Pt, tolerance = 0): boolean {
  const [bx, bz] = map.base;
  return (bx * z - bz * x) / Math.hypot(bx, bz) <= tolerance;
}

export type ExpandedMap = {
  name: string;
  bounds: { x: number; z: number };
  pointRadius: number;
  bases: { a: Pt; b: Pt };
  points: { id: string; pos: Pt }[];
  dens: Den[];
  objects: MapObjects;
};

/** Full map in a fixed order every consumer relies on: points A, B, C; dens [authored, mirror]; each
    object collection as centre objects, then the authored half, then its mirror (all in file order). */
export function expandMap(map: MapData): ExpandedMap {
  const mirrored = mirrorObjects(map.half);
  const objects = {} as Record<keyof MapObjects, unknown[]>;
  for (const key of Object.keys(map.half) as (keyof MapObjects)[]) {
    objects[key] = [...map.center[key], ...map.half[key], ...mirrored[key]];
  }
  return {
    name: map.name,
    bounds: { x: map.width / 2, z: map.height / 2 },
    pointRadius: map.pointRadius,
    bases: { a: map.base, b: mirrorPt(map.base) },
    points: [{ id: 'A', pos: map.points.side }, { id: 'B', pos: map.points.center }, { id: 'C', pos: mirrorPt(map.points.side) }],
    dens: [map.den, { kind: map.den.kind === 'griffin' ? 'behemoth' : 'griffin', pos: mirrorPt(map.den.pos) }],
    objects: objects as MapObjects,
  };
}
